/* eslint-disable */
import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
// Chakra imports
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  Spinner
} from "@chakra-ui/react";
import axios from "axios";
import { ENDPOINTS } from "utils/Constants";
import { GoogleLogin } from "@react-oauth/google";
import { GoogleOAuthProvider } from "@react-oauth/google";
// Custom components
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
import { notify } from "components/ToastNotification";
// Assets
import illustration from "assets/img/auth/auth.png";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { GOOGLE_CLIENT_ID } from "utils/Constants";
import TurnstileCaptcha from "components/captcha/TurnstileCaptcha";

function SignIn() {
  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const [loading, setLoading] = useState(false);
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const googleText = useColorModeValue("navy.700", "white");
  const googleHover = useColorModeValue(
    { bg: "gray.200" },
    { bg: "whiteAlpha.300" }
  );
  const googleActive = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.200" }
  );
  const [show, setShow] = React.useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [captchaResetSignal, setCaptchaResetSignal] = useState(0);
  const handleClick = () => setShow(!show);
  const handleCaptchaToken = React.useCallback((token) => {
    setTurnstileToken(token || "");
  }, []);

  const navigate = useNavigate();

  const [formData, setFormData] = React.useState({
    username: "",
    password: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const emptyFields = Object.keys(formData).filter((key) => !formData[key].trim());
    if (emptyFields.length > 0) {
      notify(`Please fill in all fields: ${emptyFields.join(", ")}`, "warning");
      setLoading(false);
      return;
    }
    if (!turnstileToken) {
      notify("Please complete the captcha.", "warning");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(ENDPOINTS.SIGNIN, {
        ...formData,
        turnstileToken,
      });
      const responseData = response.data;
      notify(responseData.output, responseData.success ? 'success' : 'error');
      localStorage.setItem('currentUser', JSON.stringify(responseData.data));
      localStorage.setItem('token', responseData.data.token);
      if (responseData.success) {
        window.location.href = '/admin';
      } else {
        setCaptchaResetSignal((prev) => prev + 1);
      }
    } catch (error) {
      if (error.response?.data) {
        const response = error.response?.data;
        notify(response.output, response.success ? 'success' : 'error');
      }
      setCaptchaResetSignal((prev) => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {

    try {
      setLoading(true);
      const response = await axios.post(ENDPOINTS.GOOGLE_SIGNIN, {
        token: credentialResponse.credential,
      });
      const responseData = response.data;
      notify(responseData.output, responseData.success ? "success" : "error");
      if (responseData.success) {
        localStorage.setItem("currentUser", JSON.stringify(responseData.data));
        localStorage.setItem("token", responseData.data.token);
        window.location.href = '/admin';
      }
    } catch (error) {
      notify(error.response?.data?.output || "Google Login Failed", "error");
    } finally {
      setLoading(false)
    }
  };


  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>

      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w='100%'
        mx={{ base: "auto", lg: "0px" }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection='column'>
        <Box me='auto'>
          <Heading color={textColor} fontSize='36px' mb='10px'>
            Sign In
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color={textColorSecondary}
            fontWeight='400'
            fontSize='md'>
            Enter your email and password to sign in!
          </Text>
        </Box>
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={() => notify("Google Login Failed", "error")}
            />
          </GoogleOAuthProvider>

          <Flex align='center' mb='25px'>
            <HSeparator />
            <Text color='gray.400' mx='14px'>
              or
            </Text>
            <HSeparator />
          </Flex>
          <form onSubmit={handleSubmit}>
            <FormControl>
              <Input
                isRequired={true}
                variant='auth'
                fontSize='sm'
                ms={{ base: "0px", md: "0px" }}
                type='text'
                placeholder='Username*'
                mb='24px'
                fontWeight='500'
                size='lg'
                name="username"
                value={formData.username}
                onChange={handleChange}
              />
              <InputGroup size='md'>
                <Input
                  isRequired={true}
                  fontSize='sm'
                  placeholder='Password*'
                  mb='24px'
                  size='lg'
                  type={show ? "text" : "password"}
                  variant='auth'
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <InputRightElement display='flex' alignItems='center' mt='4px'>
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: "pointer" }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>
              <Flex justifyContent='space-between' align='center' mb='24px'>
                <FormControl display='flex' alignItems='center'>
                  <Checkbox
                    id='remember-login'
                    colorScheme='brandScheme'
                    me='10px'
                  />
                  <FormLabel
                    htmlFor='remember-login'
                    mb='0'
                    fontWeight='normal'
                    color={textColor}
                    fontSize='sm'>
                    Keep me logged in
                  </FormLabel>
                </FormControl>
                <NavLink to='/auth/forgot-password'>
                  <Text
                    color={textColorBrand}
                    fontSize='sm'
                    w='124px'
                    fontWeight='500'>
                    Forgot password?
                  </Text>
                </NavLink>
              </Flex>
              <TurnstileCaptcha
                onTokenChange={handleCaptchaToken}
                resetSignal={captchaResetSignal}
              />
              <Button
                fontSize='sm'
                variant='brand'
                fontWeight='500'
                type="submit"
                w='100%'
                h='50'
                mb='24px'>
                {loading ? <Spinner size="sm" /> : "Sign In"}
              </Button>
            </FormControl>
          </form>
          <Flex
            flexDirection='column'
            justifyContent='center'
            alignItems='start'
            maxW='100%'
            mt='0px'>
            <Text color={textColorDetails} fontWeight='400' fontSize='14px'>
              Not registered yet?
              <NavLink to='/auth/sign-up'>
                <Text
                  color={textColorBrand}
                  as='span'
                  ms='5px'
                  fontWeight='500'>
                  Create an Account
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>

    </DefaultAuth >
  );
}

export default SignIn;
